import {
  Address,
  DateTimeISO,
  Money,
  PaginatedResponse,
  Status,
  StatusQueryParam,
} from './common';
import { ShortCompany } from './companies';
import { CustomerForSelectItem } from './customers';
import { TeammateForSelectItem } from './teammates';

export type PublicBusiness = {
  uuid: string;
  name: string;
};

export enum BusinessDocumentType {
  COPY_OF_EIN_LETTER = 'Copy of EIN Letter',
  W9 = 'W9',
  ARTICLES_OF_INCORPORATION = 'Articles of Incorporation',
  ARTICLES_OF_ORGANIZATION = 'Articles of Organization',
  CERTIFICATE_OF_PARTNERSHIP = 'Certificate of Partnership',
  BYLAWS = 'Bylaws',
  OPERATING_AGREEMENT = 'Operating Agreement',
  PARTNERSHIP_AGREEMENT = 'Partnership Agreement',
  CORPORATE_RESOLUTION = 'Corporate Resolution',
  CERTIFICATE_OF_REGISTRATION_WITH_THE_STATE = 'Certificate of Registration with the State',
  CERTIFICATE_OF_GOOD_STANDING = 'Certificate of Good Standing',
  CERTIFICATE_OF_TRUST = 'Certificate of Trust',
  TRUSTEE_AUTHORIZATION_LETTER_OF_CERTIFICATION = 'Trustee Authorization Letter of Certification',
  IRS_CP_575_LETTER = 'IRS CP 575 Letter',
  LETTER_147C = 'Letter 147C',
  SS_4 = 'SS 4',
}

export enum LegalStructure {
  CORP = 1,
  ESTATE = 2,
  LLC = 4,
  LLP = 5,
  NONPROFIT = 7,
  SOLEPROPRIETORSHIP = 11,
  TRUST = 12,
}

export type BusinessesAggregateInfo = {
  totalMembers: number;
  totalActiveBankAccounts: number;
  totalAvailableBalance: Money;
};

export type BusinessesListItem = {
  uuid: string;
  legalBusinessName: string;
  status: Status;
  company: {
    id: string;
    uuid: string;
    name: string;
  };
  aggregateInfo: BusinessesAggregateInfo;
  controlPerson: {
    firstName: string;
    lastName: string;
    fullName: string;
    email: string;
  };
};

export type BusinessesListResponse = {
  businesses: PaginatedResponse<BusinessesListItem>;
  aggregateInfo: BusinessesAggregateInfo;
};

export type BusinessMemberDetails = {
  uuid: string;
  consumer: CustomerForSelectItem;
  consumerId: string;
  ownershipPercentage: string;
  title: string;
  isControlPerson: boolean;
};

export type BusinessDetails = {
  uuid: string;
  legalBusinessName: string;
  dbaName: string;
  legalStructure: LegalStructure;
  tin: string;
  status: Status;
  kybStatus: string;
  createdAt: DateTimeISO;
  company?: ShortCompany;
  currentAddress: Address;
  mailingAddress: Address;
  isMailingAddressSameCurrentAddr: boolean;
  members: BusinessMemberDetails[];
  advisors?: TeammateForSelectItem[];
  aggregateInfo: BusinessesAggregateInfo;
  controlPerson: CustomerForSelectItem;
};

export type BusinessForSelectItem = {
  uuid: string;
  legalBusinessName: string;
};

export type MemberFormFields = {
  consumer: string;
  ownershipPercentage: string;
  title: string;
  isControlPerson: boolean;
};

export type MemberForm = {
  uuid: string;
  members: MemberFormFields[];
};

export type BusinessFormFields = {
  uuid: string;
  name: string;
  dbaName: string;
  legalStructure: string;
  tin: string;
  confirmTin: string;
  advisors: string[];
  currentAddress: Address;
  mailingAddress: Address;
  sameMailingAddress: boolean;
  company: string;
  companyName?: string;
  members: MemberFormFields[];
};

export type BusinessDocument = {
  uuid: string;
  filename: string;
  url: string;
  businessId: string;
  businessDocumentType: BusinessDocumentType;
};

export type BusinessDocumentResponse = {
  businessDocuments: PaginatedResponse<BusinessDocument>;
};

export type AddBusinessDocumentFields = {
  url: string;
  documentDisplayName: string;
  businessId: string;
  businessDocumentType: BusinessDocumentType | '';
};

export type BusinessForSelectionQueryParams = {
  teammate_id?: string;
  status?: StatusQueryParam;
};

export const queryKeys = {
  list: ['businessesList'] as const,
  selectionList: ['businessesSelectionList'] as const,
  details: ['businessesDetails'] as const,
  documents: ['businessesDocuments'] as const,
  preflight: ['businessesPreflight'] as const,
};
